import { useEffect, useRef, useState } from "react";
import { director } from "../director";
import { unlockMedia } from "../media";
import { limitWord } from "../callmeter";
import { hangupLine } from "../callend";
import { isActive } from "../playback";
import { navigate } from "../router";
import { api } from "../api";
import { condenseTranscript } from "../chat";
import { showWork } from "../showwork";
import { get, markActivity, toast, useStore, type DirectorStatus } from "../store";
import { enterSends, typedWord } from "../typedchat";
import { ArtifactCard } from "./Artifact";
import { Icon } from "./bits";
import { CallMeter, DirectorMark, MuteButton, PauseButton, TopUpLink } from "./Director";
import { DirectorTag, usePersona } from "./DirectorPicker";
import { Iris } from "./Iris";

const WORD: Record<DirectorStatus, string> = {
  off: "Tap to talk",
  connecting: "Connecting",
  listening: "Listening",
  thinking: "Thinking",
  speaking: "Speaking",
  error: "Tap to retry",
};

const HINT: Record<DirectorStatus, string> = {
  off: "Say what you want to make. Your Director asks the rest, writes the script and makes the video with you.",
  connecting: "Opening the line to your Director.",
  listening: "Go ahead. Say it however it comes out.",
  thinking: "One moment. You can keep talking.",
  speaking: "Talk over it any time to jump in.",
  error: "The Director didn't connect. Everything below still works by tapping.",
};

export const isLive = (s: DirectorStatus) => s !== "off" && s !== "error";

const TYPED_HINT = "You're typing with your Director. Tap the light to talk out loud instead.";

/** Legal follow-up (X3): before the first call on this device, say once that calls are recorded and transcribed. */
const CALL_NOTICE_KEY = "cao.callNotice";
function callNoticeOnce() {
  try {
    if (localStorage.getItem(CALL_NOTICE_KEY)) return;
    localStorage.setItem(CALL_NOTICE_KEY, "1");
  } catch {
    return;
  }
  toast("Calls with your Director are recorded and transcribed.", "info", { label: "Privacy", run: () => navigate("/privacy") });
}

export function toggleTalk(status: DirectorStatus) {
  // a typed chat is open: the light switches it to a voice call (the chat so far carries over), it doesn't end it
  if (isLive(status) && get().directorTyped) return void talkInstead();
  if (isLive(status)) return director.stop();
  // inside the Talk tap: unlock media so the Director can play a clip later, and start the call
  unlockMedia();
  markActivity();
  callNoticeOnce();
  void director.start();
}

/** 2026-11-02: from a typed chat to a voice call, keeping what was said (carryover), inside the tap. */
export async function talkInstead() {
  unlockMedia();
  markActivity();
  callNoticeOnce();
  const s = get();
  await api.saveCarryover(condenseTranscript(s.transcript, { userName: s.me?.user.name.split(" ")[0] })).catch(() => undefined);
  await director.end();
  void director.start();
}

/** The centrepiece on Home: the iris, its state word, the live transcript and a typed fallback. */
export function TalkStage() {
  const status = useStore((s) => s.director);
  const doing = useStore((s) => s.directorDoing);
  const error = useStore((s) => s.directorError);
  const switching = useStore((s) => s.directorSwitching);
  const hasCards = useStore((s) => s.transcript.slice(-6).some((l) => l.artifact));
  const muted = useStore((s) => s.micMuted);
  const ended = useStore((s) => s.directorEnded);
  const playing = useStore((s) => isActive(s.playback));
  const code = useStore((s) => s.directorErrorCode);
  const limit = status === "error" ? limitWord(code) : null;
  const typed = useStore((s) => s.directorTyped);
  const live = isLive(status) || !!switching;
  const tw = status === "thinking" && doing ? null : typedWord(status, typed);
  const word = limit ? limit : tw ? tw : switching ? "New voice" : status === "thinking" && doing ? doing : status === "listening" && muted ? "Muted" : status === "off" && ended === "pause" ? "Paused" : status === "off" && ended ? "Call ended" : WORD[status];
  const hint =
    status === "listening" && muted
      ? "Your mic is off and the call is still open. Type below; your Director can still talk."
      : status === "off" && ended === "idle"
        ? "It ended after three quiet minutes. Tap the iris to pick up right where you left off."
        : status === "off"
          ? hangupLine(ended, playing)
          : null;
  return (
    <section className={`stage stage-${switching ? "connecting" : limit ? "off" : status}${hasCards ? " has-cards" : ""}`} aria-label="Director">
      <DirectorTag />
      <button data-action="call.talk" type="button" className="stage-iris" onClick={() => toggleTalk(status)} aria-label={typed && live ? "Talk out loud instead" : live ? "End the conversation with your Director" : "Talk to your Director"} aria-pressed={live}>
        <Iris status={status} size="100%" />
      </button>
      <div className="stage-state" aria-live="polite">
        <p className="stage-word">{word}</p>
        <p className="stage-hint">{switching ? `Reconnecting as ${switching}. Pick up right where you were.` : typed && isLive(status) ? TYPED_HINT : hint ?? (status === "error" && error ? error : HINT[status])}</p>
        {!limit && (
          <p className="stage-meter">
            <CallMeter />
          </p>
        )}
        <TopUpLink />
      </div>
      {live && (
        <div className="stage-controls">
          <MuteButton />
          <PauseButton />
          <button data-action="call.end" type="button" className="btn btn-quiet stage-end" onClick={() => director.stop()}>
            <Icon name="stop" size={14} /> {typed ? "End chat" : "End conversation"}
          </button>
        </div>
      )}
      <Transcript limit={hasCards ? 6 : 3} />
      <Composer />
    </section>
  );
}

/**
 * The conversation: text lines plus the artifact cards the Director shows as it makes them. Older text fades;
 * cards stay solid because they carry buttons.
 */
export function Transcript({ limit = 4, compact = false }: { limit?: number; compact?: boolean }) {
  const lines = useStore((s) => s.transcript);
  const name = usePersona().name;
  const shown = lines.slice(-limit);
  // a new card comes into view on its own (phones: it's usually below the fold), but never steals the page on load
  const listRef = useRef<HTMLOListElement>(null);
  const lastCard = [...shown].reverse().find((l) => l.artifact)?.id;
  const seenCard = useRef(lastCard);
  useEffect(() => {
    if (lastCard === undefined || lastCard === seenCard.current) return;
    seenCard.current = lastCard;
    listRef.current?.querySelector(`[data-line="${lastCard}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [lastCard]);
  if (!shown.length) return null;
  return (
    <ol ref={listRef} className={`transcript${compact ? " transcript-compact" : ""}`} aria-label="Conversation" aria-live="polite">
      {shown.map((l, i) =>
        l.artifact ? (
          <li key={l.id} className="line line-card" data-line={l.id}>
            <ArtifactCard a={l.artifact} k={`line-${l.id}`} compact={compact} />
          </li>
        ) : l.chip ? (
          // flag director_shows_work: what the Director just changed, and where (Show flashes it again)
          <li key={l.id} className="line line-chip">
            <span className="line-chip-icon" aria-hidden>
              <Icon name={l.chip.icon} size={16} />
            </span>
            <span className="line-chip-label">{l.text}</span>
            <button type="button" className="line-chip-show" onClick={() => void showWork(l.chip!)} aria-label={`Show: ${l.text}`}>
              Show
            </button>
          </li>
        ) : (
          <li key={l.id} className={`line line-${l.who}`} style={{ opacity: compact ? 1 : Math.max(0.42, 1 - (shown.length - 1 - i) * 0.2) }}>
            <span className="line-who">{l.who === "you" ? "You" : l.who === "director" ? name : "Studio"}</span>
            <span className="line-text">{l.text}</span>
          </li>
        ),
      )}
    </ol>
  );
}

/** The conversation on a project page: the latest lines and cards, so what the Director makes shows up here too. */
export function ChatPanel() {
  const n = useStore((s) => s.transcript.length);
  if (!n) return null;
  return (
    <section className="chat-panel" aria-labelledby="chat-h">
      <header className="section-head">
        <h2 id="chat-h">With your Director</h2>
      </header>
      <Transcript limit={4} compact />
      <Composer placeholder="Type to your Director" />
    </section>
  );
}

/** 2026-11-02: the composer grows with what's typed, like Claude's and ChatGPT's: up to about six lines, then it
 *  scrolls. Enter sends and Shift+Enter starts a new line on a keyboard; on a phone the return key makes a new line
 *  and the button sends (a phone has no Shift). Typing never opens the mic: it starts a text chat. */
const COMPOSER_MAX_PX = 168;
const coarsePointer = () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

export function Composer({ placeholder = "Or type to your Director" }: { placeholder?: string }) {
  const [text, setText] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);
  // fit the box to its text: reset to one row, then grow to the content (capped; past it the box scrolls)
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, COMPOSER_MAX_PX);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > COMPOSER_MAX_PX ? "auto" : "hidden";
  }, [text]);
  const send = () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    // the send tap unlocks media too, so a clip the Director then cues can play
    unlockMedia();
    markActivity();
    void director.send(t);
  };
  const multi = text.includes("\n") || (box.current ? box.current.scrollHeight > 56 : false);
  return (
    <form
      className={`composer${multi ? " is-multi" : ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <textarea
        ref={box}
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (!enterSends({ key: e.key, shiftKey: e.shiftKey, altKey: e.altKey, isComposing: e.nativeEvent.isComposing }, coarsePointer())) return;
          e.preventDefault();
          send();
        }}
        placeholder={placeholder}
        aria-label="Message to your Director"
        enterKeyHint={coarsePointer() ? "enter" : "send"}
        autoComplete="off"
      />
      <button type="submit" className="icon-btn composer-send" aria-label="Send" disabled={!text.trim()}>
        <Icon name="send" />
      </button>
    </form>
  );
}

/** Things you can say, as taps: each one is sent to the Director as if typed. */
export function Suggestions({ items }: { items: string[] }) {
  return (
    <ul className="suggest" aria-label="Things to try">
      {items.map((s) => (
        <li key={s}>
          <button data-action="call.suggest" type="button" className="chip" onClick={() => void director.send(s)}>
            “{s}”
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The Director everywhere else: a floating iris on phones (every screen but Home), and a block at the foot of the
 * desktop rail. Tap to talk; while live it shows the latest line.
 */
export function DirectorDock({ variant = "float" }: { variant?: "float" | "rail" }) {
  const status = useStore((s) => s.director);
  const doing = useStore((s) => s.directorDoing);
  const last = useStore((s) => s.transcript.at(-1));
  const live = isLive(status);
  const typed = useStore((s) => s.directorTyped);
  const [fresh, setFresh] = useState(false);
  const seen = useRef(last?.id);
  useEffect(() => {
    if (!last || last.id === seen.current) return;
    seen.current = last.id;
    setFresh(true);
    const t = window.setTimeout(() => setFresh(false), 7000);
    return () => window.clearTimeout(t);
  }, [last]);
  const label = typed && live ? "Talk out loud instead" : live ? "End the conversation with your Director" : "Talk to your Director";
  const word = status === "thinking" && doing ? doing : (typedWord(status, typed) ?? WORD[status]);

  if (variant === "rail")
    return (
      <div className={`rail-director rail-director-${status}`}>
        <button data-action="call.talk" type="button" className="rail-director-iris" onClick={() => toggleTalk(status)} aria-label={label} aria-pressed={live}>
          <DirectorMark status={status} />
        </button>
        <div className="rail-director-text" aria-live="polite">
          <p className="rail-director-word">{word}</p>
          <CallMeter className="rail-meter" />
          <p className={`rail-director-line${last?.who === "you" ? " is-you" : ""}`}>{live && last ? last.text : fresh && last ? last.text : "Your Director can do anything on this page with you."}</p>
        </div>
        <MuteButton small />
      </div>
    );
  // phones: the Director bar (components/Director.tsx) replaced the v2 floating iris
  return null;
}
